// External Imports
import { Fragment, useEffect, useRef, useState } from "react";
import LoadingSpinner from "../UI/LoadingSpinner";
import { MdAdd, MdSearch } from "react-icons/md";
import { requestAdminAPI } from "../../api/posh-api";
import { toTitleCase } from "../../util/format-text";

// Internal Imports

/** ========================================================================
 * * BankAccountSelector Component
 * This component renders a dropdown to select a bank account.
 * ========================================================================== */

export default function BankAccountSelector({
  onSelect,
  selected,
  label = "Bank Account",
  addHandler,
}) {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [show, setShow] = useState(false);

  // Declare Refs
  const inputRef = useRef(null);
  const valueRef = useRef(null);

  /* ====================== FETCH RECORDS  ====================== */
  const fetchRecords = async (e) => {
    e.preventDefault();
    const query = e.target.query.value?.toLowerCase();
    if (!query) return;
    try {
      setLoading(true);
      const response = await requestAdminAPI("filter", {
        table: "account",
        filters: [`account: {contains: "${query}"}`],
      });
      setRecords(response);
      setShow(true);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= FETCH RECORD ======================= */
  const fetchRecord = async () => {
    try {
      setLoading(true);
      const res = await requestAdminAPI("fetch", {
        table: "account",
        id: selected,
      });
      optionClickHandler(res);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= OPTION CLICK HANDLER ======================= */
  const optionClickHandler = (record) => {
    inputRef.current.value = toTitleCase(record.name);
    valueRef.current.value = record.id;
    setShow(false);
    onSelect(record);
  };

  // If selection is made outside of the dropdown
  useEffect(() => {
    if (selected) fetchRecord();
  }, [selected]);

  return (
    <Fragment>
      {show && (
        <div
          onClick={() => setShow(false)}
          className="absolute top-0 left-0 w-full h-full -z-1 no-scrollbar"
        ></div>
      )}
      <div className="relative grid gap-2">
        <form onSubmit={fetchRecords} className="">
          <label className="text-sm text-stone-400">{label}:</label>
          <div className="flex items-center border rounded-md border-stone-600 ">
            <input
              className="relative w-full input"
              autoComplete="off"
              name="query"
              type="text"
              autoCorrect="off"
              spellCheck="false"
              ref={inputRef}
              placeholder="Search by account number..."
            />
            <div className="px-2">
            {loading ? (
              <LoadingSpinner classes="text-primary w-6 h-6" />
            ) : (
              <div className="flex items-center space-x-2">
                <MdAdd className="icon" onClick={addHandler}/>
                <button type="submit">
                  <MdSearch className="icon" />
                </button>
              </div>
            )}
            </div>
          </div>
        </form>
        <input className="hidden" name="account" id="account" ref={valueRef} />
        {show && (
          <div className="absolute top-16 py-2 px-4 w-full overflow-scroll rounded-lg bg-stone-700 no-scrollbar max-h-[300px] z-10">
            {records.map((account) => (
              <div
                key={account.id}
                className="px-2 py-1 text-sm font-semibold rounded-md cursor-pointer text-stone-200 hover:bg-stone-600"
                onClick={() => optionClickHandler(account)}
              >
                {account.bank} ({account.type} {account.account.slice(-4)})
              </div>
            ))}
            <div
              className="flex items-center justify-between px-2 py-1 text-sm rounded-md cursor-pointer text-stone-200 hover:bg-stone-600"
              onClick={addHandler}
            >
              <p className="font-semibold">Add new bank account</p>
              <MdAdd className="text-2xl" />
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
}
